// @flow

import * as React from 'react';
import { Rifm } from 'rifm';
import FilledInput from '@material-ui/core/FilledInput';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import {
  getCountryCallingCode,
  parsePhoneNumber,
  formatIncompletePhoneNumber,
} from 'libphonenumber-js';
import { useLocale } from '../hooks/locale.js';
import { useUserData } from '../hooks/user-data.js';
import { useSelectionBound } from '../hooks/selection-bound.js';

type Props = {|
  variant?: 'filled' | 'outlined',
  value: string,
  onChange: string => void,
  onBlur?: () => void,
|};

const formatPhone = (str: string): string => {
  const clean = str.replace(/[^\d]+/gi, '').substr(0, 12);

  const formatted = formatIncompletePhoneNumber(`+${clean}`);

  return formatted;
};

export const usePhone = (): ({|
  initialPhonePrefix: string,
  validatePhone: (phone: string) => null | string,
|}) => {
  const { t } = useLocale();
  const { countryCode } = useUserData();
  const initialPhonePrefix = `+${getCountryCallingCode(countryCode)} `;

  const validatePhone = (phone: string) => {
    if (phone.length === 0) {
      return null;
    }

    try {
      const parsed = parsePhoneNumber(phone);
      if (parsed.isPossible() === false) {
        return t('phoneInvalidNumberError');
      }
    } catch (error) {
      switch (error.message) {
        case 'INVALID_COUNTRY':
          return t('phoneInvalidCountryCodeError');
        case 'NOT_A_NUMBER':
          return t('phoneInvalidNumberError');
        case 'TOO_LONG':
          return t('phoneTooLongError');
        case 'TOO_SHORT':
          return t('phoneTooShortError');
      }
    }
    return null;
  };

  return {
    initialPhonePrefix,
    validatePhone,
  };
};

export const PhoneInput = (props: Props): React.Node => {
  const phoneValue = formatPhone(props.value);
  const inputRef = React.useRef(null);
  const { variant = 'outlined' } = props;

  useSelectionBound(inputRef, 1, -1);

  return (
    <Rifm
      accept={/[\d+]+/g}
      format={formatPhone}
      value={phoneValue}
      onChange={v => props.onChange(v === '+' ? '' : v)}
    >
      {({ value, onChange }) =>
        variant === 'filled' ? (
          <FilledInput
            inputRef={inputRef}
            type="tel"
            autoComplete="tel"
            value={value}
            onChange={onChange}
            onBlur={props.onBlur}
          />
        ) : (
          <OutlinedInput
            inputRef={inputRef}
            labelWidth={0}
            type="tel"
            autoComplete="tel"
            value={value}
            onChange={onChange}
            onBlur={props.onBlur}
          />
        )
      }
    </Rifm>
  );
};
